import { useState, useEffect } from 'react';
import api from '../lib/api';

/**
 * UWB Auto-Calibration Modal
 * Collects distance data between anchors and calculates positions
 */
export default function UWBCalibration({ warehouseId, onClose, onComplete }) {
  const [status, setStatus] = useState('idle'); // idle, starting, collecting, calculating, completed, error
  const [session, setSession] = useState(null);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);

  // Poll for calibration status
  useEffect(() => {
    if (status === 'collecting') {
      console.log('🔄 [CALIBRATION] Starting status polling for warehouse:', warehouseId);
      let pollCount = 0;
      
      const interval = setInterval(async () => {
        pollCount++;
        try {
          console.log(`📡 [CALIBRATION] Polling status (attempt ${pollCount})...`);
          const data = await api.get(`/api/uwb/calibration/status/${warehouseId}`);
          console.log(`📊 [CALIBRATION] Status response (poll ${pollCount}):`, data);
          
          if (data?.success && data.session) {
            console.log('✅ [CALIBRATION] Session data received:', {
              status: data.session.status,
              anchors: data.session.anchors?.length || 0,
              progress: data.session.progress
            });
            
            setSession(data.session);
            if (data.session.progress) {
              const newProgress = data.session.progress.overallProgress;
              console.log(`📈 [CALIBRATION] Progress: ${newProgress}% (${data.session.progress.totalReadings}/${data.session.progress.targetTotal} readings)`);
              setProgress(newProgress);
            }
            
            // Log anchor status
            if (data.session.anchors) {
              data.session.anchors.forEach((anchor, idx) => {
                console.log(`   📍 Anchor ${idx + 1}: ${anchor.name} - ${anchor.readingsCollected || 0} readings, ${Math.round((anchor.confidence || 0) * 100)}% confidence`);
              });
            }
          } else {
            console.warn('⚠️ [CALIBRATION] Status response missing session data:', data);
          }
        } catch (err) {
          console.error(`❌ [CALIBRATION] Status poll ${pollCount} failed:`, err);
          console.error('📋 [CALIBRATION] Error details:', {
            message: err.message,
            status: err.status,
            data: err.data
          });
        }
      }, 1000); // Poll every second

      return () => {
        console.log('🛑 [CALIBRATION] Stopping status polling');
        clearInterval(interval);
      };
    }
  }, [status, warehouseId]);

  const startCalibration = async () => {
    try {
      setStatus('starting');
      setError(null);
      
      console.log('🚀 [CALIBRATION] Starting calibration for warehouse:', warehouseId);
      const data = await api.post(`/api/uwb/calibration/start/${warehouseId}`, {});
      console.log('✅ [CALIBRATION] Start response received:', data);
      console.log('📊 [CALIBRATION] Response data:', JSON.stringify(data, null, 2));
      
      if (data?.success) {
        console.log('✅ [CALIBRATION] Session created:', data.session);
        console.log('📋 [CALIBRATION] Anchors in session:', data.session?.anchors?.length || 0);
        setSession(data.session);
        setStatus('collecting');
        console.log('🔄 [CALIBRATION] Status changed to: collecting');
      } else {
        console.error('❌ [CALIBRATION] Start failed - no success flag');
        throw new Error(data?.message || 'Failed to start calibration');
      }
    } catch (err) {
      console.error('❌ [CALIBRATION] Start error:', err);
      console.error('📋 [CALIBRATION] Error details:', {
        message: err.message,
        status: err.status,
        data: err.data,
        stack: err.stack
      });
      const errorMessage = err.data?.message || err.data?.error || err.message || 'Failed to start calibration';
      setError(`${errorMessage}${err.status ? ` (Status: ${err.status})` : ''}`);
      setStatus('error');
    }
  };

  const calculatePositions = async () => {
    try {
      console.log('🧮 [CALIBRATION] Starting position calculation for warehouse:', warehouseId);
      setStatus('calculating');
      setError(null);
      
      const data = await api.post(`/api/uwb/calibration/calculate/${warehouseId}`, {});
      console.log('✅ [CALIBRATION] Calculate response:', data);
      console.log('📊 [CALIBRATION] Result data:', JSON.stringify(data, null, 2));
      
      if (data?.success) {
        console.log('✅ [CALIBRATION] Positions calculated successfully!');
        console.log('📍 [CALIBRATION] Anchor positions:', data.result?.anchorPositions);
        console.log('📏 [CALIBRATION] Detected scale:', data.result?.scale);
        setStatus('completed');
        if (onComplete) {
          onComplete(data.result);
        }
      } else {
        console.error('❌ [CALIBRATION] Calculation failed - no success flag');
        throw new Error(data?.message || 'Failed to calculate positions');
      }
    } catch (err) {
      console.error('❌ [CALIBRATION] Calculate positions error:', err);
      console.error('📋 [CALIBRATION] Error details:', {
        message: err.message,
        status: err.status,
        data: err.data,
        stack: err.stack
      });
      const errorMessage = err.data?.message || err.data?.error || err.message || 'Failed to calculate positions';
      setError(errorMessage);
      setStatus('error');
    }
  };

  const cancelCalibration = async () => {
    try {
      await api.post(`/api/uwb/calibration/cancel/${warehouseId}`);
      onClose();
    } catch (err) {
      console.error('Failed to cancel calibration:', err);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl p-6 max-w-2xl w-full border border-gray-700 shadow-2xl">
        <h2 className="text-xl font-bold text-white mb-4">
          🎯 UWB Auto-Calibration
        </h2>

        {/* Idle State */}
        {status === 'idle' && (
          <div className="space-y-4">
            <div className="text-gray-300 text-sm space-y-2">
              <p>This will automatically calibrate your warehouse using UWB anchor distances.</p>
              <p className="font-semibold">How it works:</p>
              <ol className="list-decimal list-inside space-y-1 ml-2">
                <li>Anchors measure distances to each other</li>
                <li>System collects 50+ readings per anchor pair</li>
                <li>Positions are calculated using trilateration</li>
                <li>Warehouse scale is automatically determined</li>
              </ol>
              <div className="bg-blue-900/30 border border-blue-700 rounded-md p-3 mt-4">
                <p className="text-blue-300 font-semibold mb-2">📡 How to send calibration readings:</p>
                <p className="text-blue-200 text-xs">
                  Send distance readings to <code className="bg-blue-900/50 px-1 rounded">/api/uwb/readings</code> where:
                </p>
                <ul className="text-blue-200 text-xs list-disc list-inside mt-2 space-y-1">
                  <li><code className="bg-blue-900/50 px-1 rounded">hub_id</code> = Source anchor ID (e.g., "A43F")</li>
                  <li><code className="bg-blue-900/50 px-1 rounded">tag_id</code> = Target anchor ID (e.g., "B52C")</li>
                  <li><code className="bg-blue-900/50 px-1 rounded">distance</code> = Distance in meters</li>
                </ul>
                <p className="text-blue-200 text-xs mt-2">
                  Example: <code className="bg-blue-900/50 px-1 rounded">{"{hub_id: 'A43F', tag_id: 'B52C', distance: 15.23}"}</code>
                </p>
              </div>
            </div>
            
            <div className="flex gap-3 justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-md text-sm bg-gray-700 text-white border border-gray-600 hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={startCalibration}
                className="px-4 py-2 rounded-md text-sm bg-green-600 text-white border border-green-500 hover:bg-green-500"
              >
                Start Calibration
              </button>
            </div>
          </div>
        )}

        {/* Starting State */}
        {status === 'starting' && (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
            <p className="text-gray-300">Initializing calibration...</p>
          </div>
        )}

        {/* Collecting State */}
        {status === 'collecting' && session && (
          <div className="space-y-4">
            <div className="text-gray-300 text-sm mb-4">
              <p className="font-semibold mb-2">Collecting distance data...</p>
              <p className="text-xs text-gray-400">
                Anchors are measuring distances to each other. This may take 1-2 minutes.
              </p>
            </div>

            {/* Overall Progress */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Overall Progress</span>
                <span className="text-white font-semibold">{progress}%</span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-3">
                <div 
                  className="bg-green-500 h-3 rounded-full transition-all duration-500"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
            </div>

            {/* Anchor Progress */}
            <div className="space-y-2 max-h-60 overflow-y-auto">
              <p className="text-xs text-gray-400 mb-2">Anchor Status:</p>
              {session.anchors && session.anchors.map((anchor, idx) => (
                <div key={idx} className="flex items-center gap-3 bg-gray-700/50 p-2 rounded">
                  <div className="flex-1">
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-gray-300">{anchor.name}</span>
                      <span className="text-gray-400">{anchor.readingsCollected || 0} readings</span>
                    </div>
                    <div className="w-full bg-gray-600 rounded-full h-1.5">
                      <div 
                        className="bg-blue-500 h-1.5 rounded-full transition-all"
                        style={{ width: `${(anchor.confidence || 0) * 100}%` }}
                      ></div>
                    </div>
                  </div>
                  {anchor.confidence >= 0.8 && (
                    <span className="text-green-400 text-xs">✓</span>
                  )}
                </div>
              ))}
            </div>

            {/* Stats */}
            {session.progress && (
              <div className="grid grid-cols-3 gap-2 text-center text-xs">
                <div className="bg-gray-700/50 p-2 rounded">
                  <div className="text-gray-400">Total Readings</div>
                  <div className="text-white font-semibold">{session.progress.totalReadings}</div>
                </div>
                <div className="bg-gray-700/50 p-2 rounded">
                  <div className="text-gray-400">Anchor Pairs</div>
                  <div className="text-white font-semibold">{session.progress.pairsWithData}/{session.progress.totalPairs}</div>
                </div>
                <div className="bg-gray-700/50 p-2 rounded">
                  <div className="text-gray-400">Target</div>
                  <div className="text-white font-semibold">{session.progress.targetTotal}</div>
                </div>
              </div>
            )}

            <div className="flex gap-3 justify-end pt-4">
              <button
                onClick={cancelCalibration}
                className="px-4 py-2 rounded-md text-sm bg-gray-700 text-white border border-gray-600 hover:bg-gray-600"
              >
                Cancel
              </button>
              <button
                onClick={calculatePositions}
                disabled={progress < 50}
                className={`px-4 py-2 rounded-md text-sm border ${
                  progress >= 50
                    ? 'bg-green-600 text-white border-green-500 hover:bg-green-500'
                    : 'bg-gray-700 text-gray-400 border-gray-600 cursor-not-allowed'
                }`}
              >
                {progress >= 80 ? 'Calculate Positions' : `Calculate (${progress}%)`}
              </button>
            </div>
          </div>
        )}

        {/* Calculating State */}
        {status === 'calculating' && (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
            <p className="text-gray-300">Calculating anchor positions...</p>
            <p className="text-gray-400 text-sm mt-2">This may take a few seconds</p>
          </div>
        )}

        {/* Completed State */}
        {status === 'completed' && (
          <div className="text-center py-8 space-y-4">
            <div className="text-green-500 text-5xl mb-4">✓</div>
            <p className="text-white font-semibold text-lg">Calibration Complete!</p>
            <p className="text-gray-300 text-sm">
              Anchor positions have been calculated and saved.
            </p>
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-md text-sm bg-green-600 text-white border border-green-500 hover:bg-green-500"
            >
              Done
            </button>
          </div>
        )}

        {/* Error State */}
        {status === 'error' && (
          <div className="space-y-4">
            <div className="bg-red-900/30 border border-red-700 rounded-md p-4">
              <p className="text-red-400 font-semibold mb-2">Error</p>
              <p className="text-red-300 text-sm">{error}</p>
            </div>
            <div className="flex gap-3 justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-md text-sm bg-gray-700 text-white border border-gray-600 hover:bg-gray-600"
              >
                Close
              </button>
              <button
                onClick={() => setStatus('idle')}
                className="px-4 py-2 rounded-md text-sm bg-green-600 text-white border border-green-500 hover:bg-green-500"
              >
                Try Again
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
